<script setup>
// Modelo importado: código (CodeMirror) à esquerda, prévia do servidor à
// direita (CSS inline e descadastro já aplicados), salvamento automático
// com nova tentativa (2s, 4s, 8s, 16s, 30s) e aviso ao fechar a aba sem salvar.
import { ref, shallowRef, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import SendTestDialog from './SendTestDialog.vue';
import ConvertToLayoutDialog from './ConvertToLayoutDialog.vue';

const props = defineProps({
  templateId: { type: [String, Number], required: true },
});

const RETRY_DELAYS = [2000, 4000, 8000, 16000, 30000];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const host = ref(null);
const view = shallowRef(null);
const template = ref(null);
const source = ref('');
const previewHtml = ref('');
const sizeKb = ref(0);
const device = ref('desktop');
const saveState = ref('saved');
const testDialog = ref(null);
const convertDialog = ref(null);
let previewTimer = null;
let saveTimer = null;
let inFlight = null;
let resave = false;
let disposed = false;
let previewSeq = 0;
let retry = 0;
const loadFailed = ref(false);

const refreshPreview = async () => {
  previewSeq += 1;
  const seq = previewSeq;
  try {
    const { data } = await API.preview(
      { mode: 'html', source_html: source.value },
      { dark: false }
    );
    if (disposed || seq !== previewSeq) return;
    previewHtml.value = data.html;
    sizeKb.value = data.size_kb;
  } catch (error) {
    if (disposed || seq !== previewSeq) return;
    useAlert(error?.response?.data?.error || t('EMAIL_TEMPLATES.SAVE_ERROR'));
  }
};

// Um salvamento por vez; se algo mudou durante o envio, salva de novo.
const runSave = async () => {
  do {
    resave = false;
    saveState.value = 'saving';
    try {
      // eslint-disable-next-line no-await-in-loop
      await API.update(props.templateId, {
        marketing_email_template: {
          name: template.value.name,
          subject: template.value.subject,
          source_html: source.value,
        },
      });
      retry = 0;
      if (!resave && !disposed) saveState.value = 'saved';
    } catch (error) {
      if (disposed) return;
      saveState.value = 'error';
      if (retry === 0) {
        useAlert(
          error?.response?.data?.error || t('EMAIL_TEMPLATES.SAVE_ERROR')
        );
      }
      const delay = RETRY_DELAYS[Math.min(retry, RETRY_DELAYS.length - 1)];
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => save(), delay); // eslint-disable-line no-use-before-define
      retry += 1;
      return;
    }
  } while (resave);
};
const save = () => {
  clearTimeout(saveTimer);
  if (!template.value) return Promise.resolve();
  if (inFlight) {
    resave = true;
    return inFlight;
  }
  inFlight = runSave().finally(() => {
    inFlight = null;
  });
  return inFlight;
};
const touch = () => {
  saveState.value = 'dirty';
  clearTimeout(previewTimer);
  previewTimer = setTimeout(refreshPreview, 500);
  clearTimeout(saveTimer);
  saveTimer = setTimeout(save, 1500);
};
const onCode = value => {
  source.value = value;
  touch();
};
const updateTemplate = patch => {
  template.value = { ...template.value, ...patch };
  touch();
};
const flush = async () => {
  clearTimeout(previewTimer);
  if (saveState.value !== 'saved' || inFlight) await save();
};
// O "Original" do diálogo é a prévia: salva e atualiza antes de converter.
const prepareConvert = async () => {
  await flush();
  await refreshPreview();
};
const goBack = async () => {
  await flush();
  router.push({
    name: 'email_templates_index',
    params: { accountId: route.params.accountId },
  });
};

const onBeforeUnload = event => {
  if (['dirty', 'saving', 'error'].includes(saveState.value)) {
    event.preventDefault();
    event.returnValue = t('EMAIL_TEMPLATES.EDITOR.LEAVE_CONFIRM'); // eslint-disable-line no-param-reassign
  }
};

onMounted(async () => {
  window.addEventListener('beforeunload', onBeforeUnload);
  let EditorView;
  let basicSetup;
  let html;
  try {
    const { data } = await API.show(props.templateId);
    [{ EditorView, basicSetup }, { html }] = await Promise.all([
      import('codemirror'),
      import('@codemirror/lang-html'),
    ]);
    if (disposed) return;
    template.value = data;
    source.value = data.source_html || '';
  } catch (error) {
    if (disposed) return;
    loadFailed.value = true;
    useAlert(t('EMAIL_TEMPLATES.LOAD_ERROR'));
    return;
  }
  view.value = new EditorView({
    doc: source.value,
    parent: host.value,
    extensions: [
      basicSetup,
      html(),
      EditorView.theme({ '&': { height: '100%' } }),
      EditorView.updateListener.of(update => {
        if (update.docChanged) onCode(update.state.doc.toString());
      }),
    ],
  });
  refreshPreview();
});
// Ao sair: para as novas tentativas e faz UMA última tentativa de salvar.
onBeforeUnmount(() => {
  disposed = true;
  window.removeEventListener('beforeunload', onBeforeUnload);
  clearTimeout(saveTimer);
  view.value?.destroy();
  flush();
});
const inputClass =
  'rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm text-n-slate-12';
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <header
      class="flex items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2"
    >
      <button
        type="button"
        class="flex items-center gap-1 text-sm text-n-slate-11"
        @click="goBack"
      >
        <span class="i-lucide-arrow-left" /> {{ t('EMAIL_TEMPLATES.BACK') }}
      </button>
      <template v-if="template">
        <input
          :value="template.name"
          :class="inputClass"
          class="w-48"
          @input="updateTemplate({ name: $event.target.value })"
        />
        <input
          :value="template.subject"
          :placeholder="t('EMAIL_TEMPLATES.SUBJECT')"
          :class="inputClass"
          class="min-w-0 flex-1"
          @input="updateTemplate({ subject: $event.target.value })"
        />
        <button
          type="button"
          data-test="convert"
          class="flex items-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-sm text-n-slate-11 hover:border-n-brand hover:text-n-brand"
          @click="convertDialog?.open()"
        >
          <span class="i-lucide-wand-sparkles" />
          {{ t('EMAIL_TEMPLATES.EDITOR.HTML_MODE.CONVERT') }}
        </button>
        <button
          type="button"
          class="rounded-lg border border-n-weak px-2.5 py-1.5 text-sm text-n-slate-11"
          @click="testDialog?.open()"
        >
          {{ t('EMAIL_TEMPLATES.BUILDER.ACTIONS.TEST') }}
        </button>
        <span class="text-[11px] text-n-slate-10">{{
          t(`EMAIL_TEMPLATES.BUILDER.SAVE_STATE.${saveState}`)
        }}</span>
        <button
          type="button"
          class="rounded-lg bg-n-brand px-3 py-1.5 text-sm font-medium text-white"
          @click="save"
        >
          {{ t('EMAIL_TEMPLATES.SAVE') }}
        </button>
      </template>
    </header>
    <p v-if="loadFailed" class="p-6 text-sm text-n-slate-11">
      {{ t('EMAIL_TEMPLATES.LOAD_ERROR') }}
    </p>
    <div v-else class="flex min-h-0 flex-1">
      <section class="flex w-1/2 flex-col border-r border-n-weak">
        <div
          class="border-b border-n-weak px-3 py-1.5 text-xs font-semibold text-n-slate-11"
        >
          {{ t('EMAIL_TEMPLATES.EDITOR.HTML_MODE.CODE') }}
        </div>
        <div ref="host" class="min-h-0 flex-1 overflow-auto text-sm" />
      </section>
      <section class="flex w-1/2 flex-col">
        <div class="flex items-center gap-2 border-b border-n-weak px-3 py-1.5">
          <span class="text-xs font-semibold text-n-slate-11">{{
            t('EMAIL_TEMPLATES.EDITOR.HTML_MODE.PREVIEW')
          }}</span>
          <div
            class="ml-auto flex gap-0.5 rounded-lg border border-n-weak p-0.5"
          >
            <button
              v-for="d in ['desktop', 'mobile']"
              :key="d"
              type="button"
              class="rounded-md px-2 py-0.5"
              :class="
                device === d ? 'bg-n-brand/10 text-n-brand' : 'text-n-slate-11'
              "
              @click="device = d"
            >
              <span
                :class="
                  d === 'desktop' ? 'i-lucide-monitor' : 'i-lucide-smartphone'
                "
                class="block size-4"
              />
            </button>
          </div>
          <span class="text-[11px] text-n-slate-10">{{
            t('EMAIL_TEMPLATES.BUILDER.PREVIEW.SIZE', { kb: sizeKb })
          }}</span>
        </div>
        <div
          class="flex min-h-0 flex-1 justify-center overflow-auto bg-n-solid-2 p-4"
        >
          <iframe
            sandbox=""
            class="h-full rounded-md border-0 bg-white shadow"
            :class="device === 'mobile' ? 'w-[375px]' : 'w-full'"
            :srcdoc="previewHtml"
          />
        </div>
      </section>
    </div>
    <SendTestDialog
      ref="testDialog"
      :template-id="templateId"
      :before-send="flush"
    />
    <ConvertToLayoutDialog
      ref="convertDialog"
      :template-id="templateId"
      :original-html="previewHtml"
      :before-convert="prepareConvert"
    />
  </div>
</template>
